import React, {useCallback, useEffect} from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Paper
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {title} from 'process';
import EncryptUtils from 'src/utils/EncryptUtils';
import {getLMSResultsData} from '@shared/services/service-handlers/PrivateService';
import {useAppContext} from '@context/AppContext';
import {useOnlineContext} from '../../ModulePageComponents/AdditionData/OnlineTestContext';
import {BASE_RECORD_ID} from 'src/routes/BaseRoute';
import {ConfirmDialog} from '@components/custom-dialog';
import {useBoolean} from 'src/hooks/use-boolean';
import {LoadingButton} from '@mui/lab';

interface AssessmentSummaryProps {
  setSaveRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  handlePublish?: () => void;
}

const PreLearningPublish: React.FC<AssessmentSummaryProps> = ({
  setSaveRef,
  handlePublish
}) => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const confirm = useBoolean();
  const [publishLoading, setPublishLoading] = React.useState(false);
  const {onlineData, setOnlineData} = useOnlineContext();
  const {uiFields, setUiFields} = useOnlineContext();
  const {questionsData} = useOnlineContext();
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText('assessmentLink');
      alert('Link copied to clipboard!'); // Optionally replace with a more subtle notification
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };
  const saveChanges = useCallback(async () => {
    return true; // No changes to save
  }, []);

  const onSubmit = async (datavalue: any) => {
    try {
      setPublishLoading(true);
      const operations = 'UPDATE';

      const {sql_data} = uiFields['create_sql'];
      // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
      const data: any = {
        sql_data: sql_data,
        data: {
          Publish: datavalue,
          id: onlineData.inserted_id,
          course_overview_id: uniqueId,
          Page: 4
        },
        operation: operations
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getLMSResultsData(applicationName, 'LMS', {
        tk: encryptRequestData
      });
      const transformedResult = result.data.map((item: any) => {
        return {
          ...item,
          // Parse JSON fields or set them to empty arrays if they are null
          ipRestrictions: item.ipRestrictions
            ? JSON.parse(item.ipRestrictions)
            : null
          // Add any additional transformations here if needed
        };
      });
      setOnlineData(transformedResult[0]);
      setPublishLoading(false);
      // Send data to the server
    } catch (e) {
      console.log('ee', e);
    }
  };
  return (
    <Card variant="outlined" sx={{maxWidth: 1056, m: 2}}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Hi !
        </Typography>
        <Typography variant="subtitle1" sx={{mb: 2}}>
          Here&apos;s a quick glance of your assessment
        </Typography>
        <Card sx={{maxWidth: 1000, p: 2, backgroundColor: '#b1b1606e'}}>
          <Typography variant="h6" gutterBottom component="div">
            {onlineData?.name}
          </Typography>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center">
            {onlineData.assessment_time_limit_type_id === 1 ||
            onlineData.assessment_time_limit_type_id === 2 ? (
              <>
                {onlineData.assessment_time_limit_type_id === 1 && (
                  <Grid item xs={12} sm={6} md={3} sx={{pl: '8px !important'}}>
                    <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                      <Typography variant="subtitle1" gutterBottom>
                        Time Limit
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {onlineData.overallTime_in_minutes}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        minutes
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {onlineData.assessment_time_limit_type_id === 2 && (
                  <Grid item xs={12} sm={6} md={3} sx={{pl: '8px !important'}}>
                    <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                      <Typography variant="subtitle1" gutterBottom>
                        Time Limit
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {onlineData.time_per_question_in_minutes}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        minutes per question
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3} sx={{pl: '8px !important'}}>
                  <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                    <Typography variant="subtitle1" gutterBottom>
                      Total
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {questionsData.length}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      questions
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{pl: '8px !important'}}>
                  <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                    <Typography variant="subtitle1" gutterBottom>
                      Maximum
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {questionsData.reduce(
                        (acc: any, q: {QuestionMarks_id: any}) =>
                          acc + q.QuestionMarks_id,
                        0
                      )}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      marks
                    </Typography>
                  </Paper>
                </Grid>
                {onlineData.assessment_question_layout_type_id === 1 && (
                  <Grid item xs={12} sm={6} md={3} sx={{pl: '8px !important'}}>
                    <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                      <Typography variant="subtitle1" gutterBottom>
                        Layout
                      </Typography>
                      <Typography variant="h6" color="primary">
                        One question
                      </Typography>
                      <Typography variant="h6" color="primary">
                        per page
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {onlineData.assessment_question_layout_type_id === 2 && (
                  <Grid item xs={12} sm={6} md={3} sx={{pl: '8px !important'}}>
                    <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                      <Typography variant="subtitle1" gutterBottom>
                        Layout
                      </Typography>
                      <Typography variant="h6" color="primary">
                        All questions
                      </Typography>
                      <Typography variant="h6" color="primary">
                        in one page
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={4} sx={{pl: '8px !important'}}>
                  <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                    <Typography variant="subtitle1" gutterBottom>
                      Total
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {questionsData.length}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      questions
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} sx={{pl: '8px !important'}}>
                  <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                    <Typography variant="subtitle1" gutterBottom>
                      Maximum
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {questionsData.reduce(
                        (acc: any, q: {QuestionMarks_id: any}) =>
                          acc + q.QuestionMarks_id,
                        0
                      )}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      marks
                    </Typography>
                  </Paper>
                </Grid>
                {onlineData.assessment_question_layout_type_id === 1 && (
                  <Grid item xs={12} sm={6} md={4} sx={{pl: '8px !important'}}>
                    <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                      <Typography variant="subtitle1" gutterBottom>
                        Layout
                      </Typography>
                      <Typography variant="h6" color="primary">
                        One question
                      </Typography>
                      <Typography variant="h6" color="primary">
                        per page
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {onlineData.assessment_question_layout_type_id === 2 && (
                  <Grid item xs={12} sm={6} md={4} sx={{pl: '8px !important'}}>
                    <Paper variant="outlined" sx={{p: 2, textAlign: 'center'}}>
                      <Typography variant="subtitle1" gutterBottom>
                        Layout
                      </Typography>
                      <Typography variant="h6" color="primary">
                        All questions
                      </Typography>
                      <Typography variant="h6" color="primary">
                        in one page
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Card>
        <Box
          sx={{
            mt: 2,
            backgroundColor: '#d1d1006e',
            display: 'flex',
            px: 0.5
          }}>
          {onlineData?.assessment_show_marks_type_id === null && (
            <Box sx={{display: 'flex'}}>
              <Typography variant="body2">Result preferences:</Typography>
              <Typography variant="body2" sx={{fontWeight: 'bold'}}>
                {' '}
                marks are currently hidden
              </Typography>
            </Box>
          )}
          {onlineData?.assessment_show_marks_type_id === 1 && (
            <Box sx={{display: 'flex'}}>
              <Typography variant="body2">Result preferences:</Typography>
              <Typography variant="body2" sx={{fontWeight: 'bold'}}>
                {' '}
                show only marks
              </Typography>
            </Box>
          )}
        </Box>
        {/* <Typography variant="body2" sx={{mt: 2, mb: 2}}>
          Assessment link
        </Typography>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}>
          <TextField
            label="Assessment Link"
            value="https://www.bytepad.com/assessment/1234"
            fullWidth
            variant="outlined"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyLink} edge="end">
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
              pl: '3px'
            }}>
            <Button variant="contained">Preview</Button>
          </Box>
        </div> */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}>
          <Button
            onClick={() => {
              // if (
              //   onlineData?.Publish === null ||
              //   onlineData?.Publish == false
              // ) {
              //   onSubmit(true);
              // } else {
              //   onSubmit(false);
              // }
              confirm.onTrue();
            }}
            disabled={
              onlineData?.Publish === null ||
              onlineData?.Publish == false ||
              onlineData?.Publish === undefined
                ? false
                : true
            }
            sx={{mt: 1}}
            variant="contained"
            color={
              onlineData?.Publish === null ||
              onlineData?.Publish == false ||
              onlineData?.Publish === undefined
                ? 'success'
                : 'info'
            }>
            {onlineData?.Publish === null ||
            onlineData?.Publish == false ||
            onlineData?.Publish === undefined
              ? 'Publish'
              : 'Published'}
          </Button>
        </div>
      </CardContent>
      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Publish"
        content="Are you sure want to Publish Once Published the test cannot be edited"
        action={
          <LoadingButton
            variant="contained"
            color="success"
            loading={publishLoading}
            onClick={async () => {
              await onSubmit(true);
              confirm.onFalse();
              handlePublish && handlePublish();
            }}>
            Publish
          </LoadingButton>
        }
      />
    </Card>
  );
};

export default PreLearningPublish;
